import { useMemo } from 'react';

import type { Custody, LoadingState } from '@infinex/types';
import { VAULTS_LABEL } from '@infinex/types';
import type { IconComponent } from '@infinex/ui-icons';
import { PortfolioFillIcon } from '@infinex/ui-icons';

import { useUsdBalanceTotals } from '../../../features/balances';
import type { WalletView } from '../../../hooks';
import { useDisabledCustodyConfig } from '../../../lib/custody-config';
import {
  useCustodiesListWithBalance,
  useWalletDisplayInfo,
} from '../../user/hooks';

type CustodyDisplayItemsOptions = {
  includeTotalPortfolio: boolean;
  includeVaults: boolean;
};

export type CustodyDisplayFilterId = WalletView;

export type CustodyDisplayItem = {
  id: WalletView;
  label: string;
  totalUsdBalance: number | undefined;
  totalUsdTokenOnlyBalance: number | undefined;
  icon: IconComponent; // Fallback icon for items without a walletId
  custody: Custody | undefined;
  isImported: boolean | undefined;
  walletId?: string;
  /** Whether this custody item is disabled based on disabledCustodyConfig */
  isDisabled?: boolean;
  /** Message to display when custody is disabled */
  disabledMessage?: string;
};

/**
 * Serves the flat dropdown items for the custody switcher menu.
 *
 * Disabled custody behavior:
 * - When used inside a DisabledCustodyProvider (Move, Send, Swidge, etc.),
 *   items will have `isDisabled` and `disabledMessage` set based on config.
 * - When used outside a provider (Portfolio, Asset Details, Activity),
 *   no items are disabled (safe default).
 */
export const useCustodyDisplayItems = ({
  includeTotalPortfolio,
  includeVaults,
}: CustodyDisplayItemsOptions): LoadingState<
  { walletItems: CustodyDisplayItem[] },
  { walletItems: CustodyDisplayItem[] }
> => {
  const { getDisabledConfigForCustody } = useDisabledCustodyConfig();

  const {
    totalUsdBalance: totalUsd,
    totalUsdTokenBalance: totalUsdTokenOnly,
    isLoading: isUsdTotalsLoading,
  } = useUsdBalanceTotals();

  const custodiesWithBalance = useCustodiesListWithBalance({
    excludeVaults: !includeVaults,
  });

  const isLoading = custodiesWithBalance.isLoading || isUsdTotalsLoading;
  const { getWalletDisplayInfo } = useWalletDisplayInfo();

  const walletItems = useMemo(() => {
    // Helper to apply disabled config to an item
    const applyDisabledConfig = (
      item: CustodyDisplayItem
    ): CustodyDisplayItem => {
      if (!item.custody) return item;

      const config = getDisabledConfigForCustody(
        { custody: item.custody, walletId: item.walletId },
        'source'
      );

      if (config) {
        return {
          ...item,
          isDisabled: true,
          disabledMessage: config.label ?? config.details,
        };
      }

      return item;
    };

    const items: CustodyDisplayItem[] = (
      custodiesWithBalance.value ?? []
    ).flatMap<CustodyDisplayItem>((custody) => {
      if (custody.groupKind === 'recoveryPhrase') {
        return custody.wallets.map((wallet) =>
          applyDisabledConfig({
            id: `wallet:${wallet.id}` satisfies CustodyDisplayFilterId,
            label: wallet.label,
            totalUsdBalance: wallet.totalUsdBalance.value,
            totalUsdTokenOnlyBalance: wallet.totalUsdTokenOnlyBalance.value,
            icon: getWalletDisplayInfo(wallet.id).icon,
            custody: 'wallet',
            isImported: wallet.isImported,
            walletId: wallet.id,
          })
        );
      }

      if (custody.groupKind === 'privateKey') {
        return [
          applyDisabledConfig({
            id: `wallet:${custody.wallet.id}` satisfies CustodyDisplayFilterId,
            label: custody.wallet.label,
            totalUsdBalance: custody.wallet.totalUsdBalance.value,
            totalUsdTokenOnlyBalance:
              custody.wallet.totalUsdTokenOnlyBalance.value,
            icon: getWalletDisplayInfo(custody.wallet.id).icon,
            custody: 'wallet',
            isImported: custody.wallet.isImported,
            walletId: custody.wallet.id,
          }),
        ];
      }

      if (custody.groupKind === 'externalSigningDevice') {
        return custody.wallets.map((wallet) =>
          applyDisabledConfig({
            id: `wallet:${wallet.id}` satisfies CustodyDisplayFilterId,
            label: wallet.label,
            totalUsdBalance: wallet.totalUsdBalance.value,
            totalUsdTokenOnlyBalance: wallet.totalUsdTokenOnlyBalance.value,
            icon: getWalletDisplayInfo(wallet.id).icon,
            custody: 'external-signer' as const,
            isImported: true,
            walletId: wallet.id,
          })
        );
      }

      if (custody.groupKind === 'multiSigWallet') {
        return applyDisabledConfig({
          id: `wallet:${custody.id}` satisfies CustodyDisplayFilterId,
          label: custody.label,
          totalUsdBalance: custody.totalUsdBalance.value,
          totalUsdTokenOnlyBalance: custody.totalUsdTokenOnlyBalance.value,
          icon: getWalletDisplayInfo(custody.id).icon,
          custody: 'multi-sig' as const,
          isImported: true,
          walletId: custody.id,
        });
      }

      // Vault
      return [
        applyDisabledConfig({
          id: 'vault' as const,
          label: VAULTS_LABEL,
          totalUsdBalance: custody.totalUsdBalance.value,
          totalUsdTokenOnlyBalance: custody.totalUsdTokenOnlyBalance.value,
          icon: getWalletDisplayInfo('vault').icon,
          custody: 'vault',
          isImported: false,
          walletId: undefined,
        }),
      ];
    });

    if (includeTotalPortfolio) {
      items.unshift({
        id: 'all' as const,
        label: 'Total portfolio',
        totalUsdBalance: totalUsd,
        totalUsdTokenOnlyBalance: totalUsdTokenOnly,
        icon: PortfolioFillIcon,
        custody: undefined,
        isImported: undefined,
        walletId: undefined,
      });
    }

    return items;
  }, [
    custodiesWithBalance.value,
    getDisabledConfigForCustody,
    getWalletDisplayInfo,
    includeTotalPortfolio,
    totalUsd,
    totalUsdTokenOnly,
  ]);

  if (isLoading) {
    return { isLoading: true, value: { walletItems: [] } };
  }

  return { isLoading: false, value: { walletItems } };
};
